import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Info, Cpu, Zap, X, ChevronRight, Keyboard, Link as LinkIcon, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

interface StudioOnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const StudioOnboardingModal: React.FC<StudioOnboardingModalProps> = ({ isOpen, onClose }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-zinc-950/40 backdrop-blur-md"
            onClick={onClose}
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-3xl bg-white border border-zinc-100 rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
          >
            {/* Header */}
            <div className="p-8 border-b border-zinc-50 flex items-center justify-between bg-white shrink-0">
              <div className="flex items-center gap-5">
                <div className="w-12 h-12 rounded-2xl bg-cyan-600 flex items-center justify-center shadow-lg shadow-cyan-600/20 relative overflow-hidden group">
                  <Zap className="w-6 h-6 text-white relative z-10" />
                </div>
                <div>
                  <h2 className="text-[14px] font-semibold text-zinc-950 uppercase tracking-[0.3em]">
                    CREATOR <span className="text-cyan-600">STUDIO</span>
                  </h2>
                  <p className="text-zinc-400 text-[10px] font-medium uppercase tracking-[0.2em] mt-1">
                    Your Creative Workspace
                  </p>
                </div>
              </div>
              <button 
                onClick={onClose}
                className="w-10 h-10 rounded-full hover:bg-zinc-50 flex items-center justify-center transition-all text-zinc-300 hover:text-red-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content Container */}
            <div className="overflow-y-auto custom-scrollbar bg-white">
              <div className="p-8 md:p-10 space-y-10">
                
                {/* Introduction */}
                <section className="space-y-4">
                  <div className="flex items-center gap-3 text-zinc-950">
                    <div className="p-2 bg-cyan-50 rounded-lg">
                      <Info className="w-4 h-4 text-cyan-600" />
                    </div>
                    <h3 className="font-bold uppercase tracking-[0.15em] text-[11px] text-zinc-400">Welcome</h3>
                  </div>
                  <div className="space-y-4">
                    <h4 className="text-2xl font-semibold text-zinc-950 tracking-tight leading-tight">
                      Welcome to your new Creator Studio.
                    </h4>
                    <p className="text-zinc-500 text-[14px] leading-relaxed max-w-2xl">
                      You're now inside a powerful real-time engine designed for creating unique audio-visual simulations. 
                      Use the panels to your right to tweak physics, sounds, and visuals in real-time.
                    </p>
                  </div>
                </section>

                <div className="grid md:grid-cols-2 gap-8">
                  {/* Shortcut Keys */}
                  <section className="space-y-4 p-6 bg-zinc-50/50 rounded-3xl border border-zinc-100">
                    <div className="flex items-center gap-3 text-zinc-950">
                      <div className="p-2 bg-white border border-zinc-100 rounded-lg shadow-sm">
                        <Keyboard className="w-4 h-4 text-cyan-600" />
                      </div>
                      <h3 className="font-bold uppercase tracking-[0.15em] text-[11px] text-zinc-400">Quick Shortcuts</h3>
                    </div>
                    <div className="grid grid-cols-1 gap-3">
                      <div className="flex items-center justify-between p-3 bg-white border border-zinc-100 rounded-xl">
                        <span className="text-[12px] text-zinc-600">Reset to Defaults</span>
                        <kbd className="px-2 py-1 bg-zinc-100 rounded text-[10px] font-mono font-bold text-zinc-950 border-b-2 border-zinc-300">R</kbd>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white border border-zinc-100 rounded-xl">
                        <span className="text-[12px] text-zinc-600">Play / Pause</span>
                        <kbd className="px-2 py-1 bg-zinc-100 rounded text-[10px] font-mono font-bold text-zinc-950 border-b-2 border-zinc-300">SPACE</kbd>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white border border-zinc-100 rounded-xl">
                        <span className="text-[12px] text-zinc-600">Toggle Interface</span>
                        <kbd className="px-2 py-1 bg-zinc-100 rounded text-[10px] font-mono font-bold text-zinc-950 border-b-2 border-zinc-300">ESC</kbd>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white border border-zinc-100 rounded-xl">
                        <span className="text-[12px] text-zinc-600">Quick Save Preset</span>
                        <kbd className="px-2 py-1 bg-zinc-100 rounded text-[10px] font-mono font-bold text-zinc-950 border-b-2 border-zinc-300">S</kbd>
                      </div>
                    </div>
                  </section>

                  {/* Performance Tips */}
                  <section className="space-y-4 p-6 bg-zinc-50/50 rounded-3xl border border-zinc-100">
                    <div className="flex items-center gap-3 text-zinc-950">
                      <div className="p-2 bg-white border border-zinc-100 rounded-lg shadow-sm">
                        <Cpu className="w-4 h-4 text-orange-500" />
                      </div>
                      <h3 className="font-bold uppercase tracking-[0.15em] text-[11px] text-zinc-400">Smooth Performance</h3>
                    </div>
                    <div className="space-y-4">
                      <p className="text-[12px] text-zinc-500 leading-relaxed">
                        For the best experience, ensure your browser is using your graphics card.
                      </p>
                      <div className="space-y-3">
                        <div className="flex gap-3">
                          <div className="w-5 h-5 rounded-full bg-zinc-200 flex shrink-0 items-center justify-center text-[10px] font-bold text-zinc-600">W</div>
                          <p className="text-[11px] text-zinc-500">Windows: Set browser to "High Performance" in System Graphics Settings.</p>
                        </div>
                        <div className="flex gap-3">
                          <div className="w-5 h-5 rounded-full bg-zinc-200 flex shrink-0 items-center justify-center text-[10px] font-bold text-zinc-600">M</div>
                          <p className="text-[11px] text-zinc-500">Mac: Go to Settings &gt; Battery and ensure "Automatic Graphics Switching" is on.</p>
                        </div>
                        <div className="flex gap-3">
                          <div className="w-5 h-5 rounded-full bg-zinc-200 flex shrink-0 items-center justify-center text-[10px] font-bold text-zinc-600">B</div>
                          <p className="text-[11px] text-zinc-500">Browser: Enable "Hardware Acceleration" in your browser settings menu.</p>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>

                {/* Helpful Links */}
                <section className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-50 rounded-lg">
                      <LinkIcon className="w-4 h-4 text-purple-600" />
                    </div>
                    <h3 className="font-bold uppercase tracking-[0.15em] text-[11px] text-zinc-400">Resources & Help</h3>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Link to="/plans" className="group p-4 bg-white border border-zinc-100 rounded-2xl hover:border-cyan-600/30 hover:bg-cyan-50/30 transition-all">
                      <h5 className="text-[12px] font-semibold text-zinc-900 group-hover:text-cyan-600 flex items-center justify-between">
                        Unlock Modes <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </h5>
                      <p className="text-[10px] text-zinc-400 mt-1">Upgrade your plan</p>
                    </Link>
                    <Link to="/help" className="group p-4 bg-white border border-zinc-100 rounded-2xl hover:border-cyan-600/30 hover:bg-cyan-50/30 transition-all">
                      <h5 className="text-[12px] font-semibold text-zinc-900 group-hover:text-cyan-600 flex items-center justify-between">
                        Help & Guides <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </h5>
                      <p className="text-[10px] text-zinc-400 mt-1">Learn the features</p>
                    </Link>
                    <Link to="/contact" className="group p-4 bg-white border border-zinc-100 rounded-2xl hover:border-cyan-600/30 hover:bg-cyan-50/30 transition-all">
                      <h5 className="text-[12px] font-semibold text-zinc-900 group-hover:text-cyan-600 flex items-center justify-between">
                        Get Support <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </h5>
                      <p className="text-[10px] text-zinc-400 mt-1">Connect with us</p>
                    </Link>
                    <Link to="/faq" className="group p-4 bg-white border border-zinc-100 rounded-2xl hover:border-cyan-600/30 hover:bg-cyan-50/30 transition-all">
                      <h5 className="text-[12px] font-semibold text-zinc-900 group-hover:text-cyan-600 flex items-center justify-between">
                        FAQs <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </h5>
                      <p className="text-[10px] text-zinc-400 mt-1">Common questions</p>
                    </Link>
                  </div>
                </section>
              </div>
            </div>

            {/* Footer */}
            <div className="p-8 border-t border-zinc-50 bg-zinc-50/30 flex flex-col sm:flex-row justify-between items-center gap-6 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-[10px] text-zinc-400 font-medium uppercase tracking-[0.2em]">
                  System Status: Operational
                </span>
              </div>
              <button
                onClick={onClose}
                className="w-full sm:w-auto px-10 py-4 bg-cyan-600 text-white shadow-lg shadow-cyan-600/20 hover:bg-cyan-700 hover:shadow-cyan-600/30 transition-all active:scale-95 text-[11px] font-bold uppercase tracking-[0.2em] rounded-2xl flex items-center justify-center gap-3"
              >
                Enter Studio
                <Zap className="w-4 h-4 fill-current" />
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
